import { listDatasets, type Dataset, type DatasetStatus } from "@/api";
import type {
  DataTablePageOptions,
  DataTablePageReq,
  DataTablePageResp,
} from "@carbon6/c6-frontend-design";
import { DatasetCriteriaTableSource } from "./DatasetCriteriaTableSource";
import {
  createDatasetTableRows,
  type DatasetTableRow,
  type DatasetTableRowActionBuilder,
  type DatasetTableRowClickHandler,
} from "./DatasetTableRow";

type DatasetTableSourceOptions = {
  pageSize: number;
  rowActionsBuilder: DatasetTableRowActionBuilder;
  rowClickHandler: DatasetTableRowClickHandler;
  resolveErrorMessage: (error: unknown) => string;
  // 생성 시 고정하는 status 필터. 일반 목록은 available, 보관 목록은 archived 를 명시한다.
  // null 은 서버 기본 범위가 필요한 특수 화면에서만 쓴다.
  statusFilter?: DatasetStatus | null;
  // 리뷰 큐로 좁힐지 여부. 리뷰 페이지만 true 로 두고, 일반 목록은 false(필터 없음)다.
  reviewQueueOnly?: boolean;
};

// 공용 Dataset 목록 소스. criteria(검색, 필터)는 베이스가 소유한다. 여기서는 status·리뷰 큐
// 고정 축과 GET /v1/datasets 호출, 행 조립만 정한다.
export class DatasetTableSource extends DatasetCriteriaTableSource {
  private readonly rowActionsBuilder: DatasetTableRowActionBuilder;
  private readonly rowClickHandler: DatasetTableRowClickHandler;
  // 생성 시 고정하는 status 축. 일반 목록은 available, 보관 목록은 archived 다. 실행 중 바뀌지
  // 않으므로 setter 를 두지 않는다. null 이면 서버 기본 범위가 필요한 특수 화면이라 파라미터를 보내지 않는다.
  private readonly statusFilter: DatasetStatus | null;
  // 생성 시 고정하는 리뷰 큐 축(리뷰 페이지 전용). 실행 중 바뀌지 않으므로 setter 를 두지 않는다.
  private readonly reviewQueueOnly: boolean;

  constructor({
    pageSize,
    rowActionsBuilder,
    rowClickHandler,
    resolveErrorMessage,
    statusFilter = null,
    reviewQueueOnly = false,
  }: DatasetTableSourceOptions) {
    super({
      initialPageSize: pageSize,
      resolveErrorMessage,
    });
    this.rowActionsBuilder = rowActionsBuilder;
    this.rowClickHandler = rowClickHandler;
    this.statusFilter = statusFilter;
    this.reviewQueueOnly = reviewQueueOnly;
  }

  protected override async onPage(
    req: DataTablePageReq,
    { signal }: DataTablePageOptions,
  ): Promise<DataTablePageResp<DatasetTableRow>> {
    const response = await listDatasets(
      {
        page: req.page,
        size: req.size,
        ...(this.statusFilter ? { status: this.statusFilter } : {}),
        ...(this.reviewQueueOnly ? { review_queue: "true" as const } : {}),
        ...this.criteriaQuery(),
        ...this.sortQuery(),
      },
      { signal },
    );

    return {
      rows: this.createRows(response.items),
      total: response.total,
      page: response.page,
      size: response.size,
    };
  }

  private createRows(datasets: readonly Dataset[]): DatasetTableRow[] {
    return createDatasetTableRows(datasets, this.rowActionsBuilder, this.rowClickHandler);
  }
}
